import { createQueryParams } from '@/utils/query-params';
import { api } from './index';

const BASE = '/api/internal/inventory';

export type LocationKind = 'warehouse' | 'patient' | 'supplier' | 'consumed' | 'loss' | 'adjustment';
export type Tracking = 'quantity' | 'serialized';
export type UnitOfMeasure =
  'unit' | 'pair' | 'ml' | 'g' | 'tablet' | 'capsule' | 'sachet' | 'ampoule' | 'vial' | 'syringe' |
  'patch' | 'suppository' | 'enema';
export type MovementReason =
  'entry' | 'delivery' | 'return' | 'transfer' | 'use' | 'loss' | 'count_adjustment';
export type ExitCause = 'damaged' | 'lost' | 'stolen' | 'expired' | 'other';
export type UnitCondition = 'operational' | 'needs_attention' | 'out_of_service';
export type UnitStatus = 'in_service' | 'retired' | 'returned';

export interface InventoryCategory {
  id: number;
  name: string;
  discarded: boolean;
  // Caregivers see the category's stock in the app.
  visibleToCaregivers: boolean;
}

export interface InventoryItem {
  id: number;
  name: string;
  code: string | null;
  description: string | null;
  inventoryCategoryId: number;
  categoryName: string;
  tracking: Tracking;
  humanTracking: string;
  unitOfMeasure: UnitOfMeasure;
  humanUnitOfMeasure: string;
  quantityStep: number;
  defaultUnitCostCents: number | null;
  currency: string;
  discarded: boolean;
  clinicalDrugId: number | null;
}

export interface InventoryItemForm {
  name: string;
  clinicalDrugId?: number;
  inventoryCategoryId?: number;
  tracking: Tracking;
  unitOfMeasure: UnitOfMeasure;
  defaultUnitCostCents?: number | null;
  code?: string | null;
  description?: string | null;
}

export interface InventoryLocation {
  id: number;
  kind: LocationKind;
  humanKind: string;
  name: string;
  patientId: number | null;
  discarded: boolean;
}

export interface InventoryBalance {
  id: number;
  inventoryItemId: number;
  itemName: string;
  inventoryCategoryId: number;
  categoryName: string;
  tracking: Tracking;
  unitOfMeasure: UnitOfMeasure;
  humanUnitOfMeasure: string;
  quantityStep: number;
  inventoryLocationId: number;
  quantity: number;
  minQuantity: number | null;
  lowStock: boolean;
}

export interface InventoryUnit {
  id: number;
  identifier: string | null;
  inventoryItemId: number;
  itemName: string;
  inventoryCategoryId: number;
  categoryName: string;
  inventoryLocationId: number;
  locationKind: LocationKind;
  locationName: string;
  patientId: number | null;
  ownerLocationId: number | null;
  ownerName: string | null;
  condition: UnitCondition;
  humanCondition: string;
  status: UnitStatus;
  acquisitionCostCents: number;
  currency: string;
  lotCode: string | null;
  notes: string | null;
  outOfServiceAlert: boolean;
}

export interface MovementEnd { id: number; kind: LocationKind; name: string; patientId: number | null }

export interface InventoryMovement {
  id: number;
  reason: MovementReason;
  humanReason: string;
  inventoryItemId: number;
  itemName: string;
  unitOfMeasure: UnitOfMeasure;
  fromLocation: MovementEnd;
  toLocation: MovementEnd;
  quantity: number;
  countedQuantity: number | null;
  unitIdentifier: string | null;
  lotCode: string | null;
  lotExpiresOn: string | null;
  totalCostCents: number;
  currency: string;
  occurredOn: string;
  exitCause: ExitCause | null;
  humanExitCause: string | null;
  description: string | null;
  reversesMovementId: number | null;
  reversed: boolean;
  recordedBy: string;
  createdAt: string;
}

// A location is given by id or as a patient's home. Field names follow the API:
// `fromLocationId` / `fromPatientId`, `toLocationId` / `toPatientId`, and for
// counts the unprefixed `locationId` / `patientId`.
export interface MovementForm {
  reason: MovementReason;
  inventoryItemId: number;
  quantity?: number;
  countedQuantity?: number;
  fromLocationId?: number;
  fromPatientId?: number;
  toLocationId?: number;
  toPatientId?: number;
  locationId?: number;
  patientId?: number;
  inventoryUnitId?: number;
  inventoryLotId?: number;
  totalCostCents?: number;
  exitCause?: ExitCause;
  description?: string;
  occurredOn?: string;
  lot?: { code?: string; expiresOn?: string };
  newUnit?: { identifier?: string; notes?: string; ownerLocationId?: number };
}

export interface InventoryAlert {
  id: number;
  kind: 'low_stock' | 'unit_condition';
  humanKind: string;
  inventoryItemId: number;
  itemName: string;
  inventoryLocationId: number;
  locationName: string;
  patientId: number | null;
  unitIdentifier: string | null;
  openedAt: string;
  notifiedAt: string | null;
  resolvedAt: string | null;
  // A caregiver's equipment problem report from the app.
  note: string | null;
  reportedBy: string | null;
}

export interface PlaceFilter { patientId?: number; locationId?: number }

function query(path: string, params: object = {}): string {
  const search = createQueryParams(params);

  return search ? `${BASE}/${path}?${search}` : `${BASE}/${path}`;
}

export const inventoryApi = {
  categories(scope: 'active' | 'discarded' = 'active'): Promise<InventoryCategory[]> {
    return api.get(query('categories', { scope })).then((r) => r.data.inventoryCategories);
  },
  saveCategory(
    category: { id?: number; name: string; visibleToCaregivers?: boolean },
  ): Promise<InventoryCategory> {
    const request = category.id ?
      api.put(`${BASE}/categories/${category.id}`, { inventoryCategory: category }) :
      api.post(`${BASE}/categories`, { inventoryCategory: category });

    return request.then((r) => r.data.inventoryCategory);
  },
  archiveCategory(id: number): Promise<void> {
    return api.delete(`${BASE}/categories/${id}`);
  },

  items(filters: { scope?: 'active' | 'discarded'; categoryId?: number; tracking?: Tracking } = {}):
  Promise<InventoryItem[]> {
    return api.get(query('items', filters)).then((r) => r.data.inventoryItems);
  },
  saveItem(item: InventoryItemForm & { id?: number }): Promise<InventoryItem> {
    const request = item.id ?
      api.put(`${BASE}/items/${item.id}`, { inventoryItem: item }) :
      api.post(`${BASE}/items`, { inventoryItem: item });

    return request.then((r) => r.data.inventoryItem);
  },
  archiveItem(id: number): Promise<void> {
    return api.delete(`${BASE}/items/${id}`);
  },
  restoreItem(id: number): Promise<InventoryItem> {
    return api.put(`${BASE}/items/${id}`, { inventoryItem: {} }).then((r) => r.data.inventoryItem);
  },

  locations(filters: { kinds?: LocationKind[]; patientId?: number; scope?: 'active' | 'discarded' } = {}):
  Promise<InventoryLocation[]> {
    return api.get(query('locations', filters)).then((r) => r.data.inventoryLocations);
  },
  saveLocation(location: { id?: number; kind?: 'warehouse' | 'supplier'; name: string }):
  Promise<InventoryLocation> {
    const request = location.id ?
      api.put(`${BASE}/locations/${location.id}`, { inventoryLocation: location }) :
      api.post(`${BASE}/locations`, { inventoryLocation: location });

    return request.then((r) => r.data.inventoryLocation);
  },
  archiveLocation(id: number): Promise<void> {
    return api.delete(`${BASE}/locations/${id}`);
  },

  balances(filters: PlaceFilter & { itemId?: number } = {}): Promise<InventoryBalance[]> {
    return api.get(query('balances', filters)).then((r) => r.data.inventoryBalances);
  },
  units(filters: PlaceFilter & { itemId?: number; status?: UnitStatus } = {}): Promise<InventoryUnit[]> {
    return api.get(query('units', filters)).then((r) => r.data.inventoryUnits);
  },
  updateUnit(id: number, attrs: { condition?: UnitCondition; identifier?: string; notes?: string }):
  Promise<InventoryUnit> {
    return api.put(`${BASE}/units/${id}`, { inventoryUnit: attrs }).then((r) => r.data.inventoryUnit);
  },

  movements(
    filters: PlaceFilter & { itemId?: number; shiftId?: number; from?: string; to?: string; limit?: number } = {},
  ): Promise<InventoryMovement[]> {
    return api.get(query('movements', filters)).then((r) => r.data.inventoryMovements);
  },
  recordMovement(movement: MovementForm): Promise<InventoryMovement> {
    return api.post(`${BASE}/movements`, { inventoryMovement: movement })
      .then((r) => r.data.inventoryMovement);
  },
  reverseMovement(id: number): Promise<InventoryMovement> {
    return api.post(`${BASE}/movements/${id}/reverse`).then((r) => r.data.inventoryMovement);
  },

  setMinimum(rule: PlaceFilter & { inventoryItemId: number; minQuantity: number | null }): Promise<void> {
    return api.post(`${BASE}/stock_rules`, { inventoryStockRule: rule });
  },

  alerts(filters: PlaceFilter & { status?: 'open' | 'resolved' } = {}): Promise<InventoryAlert[]> {
    return api.get(query('alerts', filters)).then((r) => r.data.inventoryAlerts);
  },
};
